import { useEffect, useState, useRef, useCallback } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import aboutImage from "../assets/images/about-bg.jpg"
import { useUserParams } from "../hooks/useUserParams";

const useAnimatedBorder = () => {
  const topLeftRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const bottomRightRef = useRef(null);
  const rightRef = useRef(null);
  const leftRef = useRef(null);

  useEffect(() => {
    const timeouts = [];

    // Top borders animation
    timeouts.push(
      setTimeout(() => {
        topLeftRef.current?.classList.remove("w-0");
        topLeftRef.current?.classList.add("w-half");
        topRightRef.current?.classList.remove("w-0");
        topRightRef.current?.classList.add("w-half");
      }, 10)
    );

    // Side borders animation
    timeouts.push(
      setTimeout(() => {
        leftRef.current?.classList.remove("h-0");
        leftRef.current?.classList.add("h-full");
        rightRef.current?.classList.remove("h-0");
        rightRef.current?.classList.add("h-full");
      }, 1000)
    );

    // Bottom borders animation
    timeouts.push(
      setTimeout(() => {
        bottomLeftRef.current?.classList.remove("w-0");
        bottomLeftRef.current?.classList.add("w-half");
        bottomRightRef.current?.classList.remove("w-0");
        bottomRightRef.current?.classList.add("w-half");
      }, 4000)
    );

    return () => timeouts.forEach((timeout) => clearTimeout(timeout));
  }, []);

  return {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  };
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = useCallback(() => {
    setWindowSize(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [onWindowResize]);

  return windowSize;
};

const useScrollVisibility = () => {
  const slideRefs = useRef([]);

  const addToRefs = (el) => {
    if (el && !slideRefs.current.includes(el)) {
      slideRefs.current.push(el);
    }
  };

  const isScrolledIntoView = useCallback((elem) => {
    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      return elemTop >= 0 && elemBottom <= window.innerHeight;
    }
    return false;
  }, []);

  useEffect(() => {
    slideRefs.current.forEach((el) => {
      if (isScrolledIntoView(el)) {
        el?.classList.remove("slide-left");
      }
    });
  }, [isScrolledIntoView]);

  return { addToRefs };
};

export default function About() {
  const windowSize = useWindowSize();
  const {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  } = useAnimatedBorder();
  const { addToRefs } = useScrollVisibility();
  const {
    getCurrentAgentName,
    getCurrentBrokerage,
    getCurrentQuote,
    getCurrentHeadshot,
  } = useUserParams();

  const seoConfig = pageSEO.about();

  return (
    <>
      <SEO {...seoConfig} />
      <div
        className="relative p-20 mx-auto mt-16 flex flex-col md:flex-row gap-y-10 gap-x-14 max-w-[1600px] min-h-[400px] rounded-[48px]"
        style={{
          backgroundImage: `url(${aboutImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div
          className="absolute inset-0 z-0 rounded-[48px]"
          style={{
            background:
              "linear-gradient(135deg, rgba(50, 97, 144, 0.85) 0%, rgba(0, 60, 120, 0.6) 30%, rgba(255, 255, 255, 0.3) 100%)",
          }}
        />
        <div className="relative z-10 flex flex-col md:flex-row gap-y-10 gap-x-14 w-full">
          <img
            src={getCurrentHeadshot()}
            alt={getCurrentAgentName()}
            className="min-w-52 rounded-full"
            width={300}
            height={300}
          />
          <div>
            <h1 className="font-mont text-white text-5xl font-bold italic pb-4">
              &ldquo;{getCurrentQuote()}&rdquo;
            </h1>
            <p className="text-white mt-3 text-3xl font-mont font-bold">
              {getCurrentAgentName()}
            </p>
            <p className="text-white mt-1 text-2xl font-mont font-bold">
              {getCurrentBrokerage()}
            </p>
          </div>
        </div>
        {/* Button at bottom right */}
        <a
          href="/about"
          className="absolute bottom-8 right-8 z-20 px-8 py-4 bg-white/90 text-logo-second font-mont font-bold text-xl rounded-full shadow-lg transition-transform duration-300 hover:scale-105 hover:bg-logo-second hover:text-white hover:shadow-2xl"
          style={{ letterSpacing: "0.05em" }}
        >
          Learn More
        </a>
      </div>
    </>
  );
}
